/**
 * The one place the product is named (2026-09-06).
 *
 * Before this file, the visible name came out of `useBranding()`, which derived
 * it from the THEME: the diary theme renamed the product to "yanamas", every
 * other theme called it "HortonIO". Changing your colours renamed your company.
 * On top of that ~450 copy strings hardcoded a name and never asked the hook at
 * all, so the rail said one thing, the chat page said another, and Today's
 * why-page credited a third. A person could not tell who was speaking.
 *
 * So: the name is a CONSTANT, not a function of anything. Themes change
 * colours. Only the artwork has a light and a dark variant, because that is a
 * real rendering concern; see `useBranding()`, which still owns the asset
 * switch and re-exports the words from here so old call sites keep working.
 *
 * THE VALUES BELOW ARE PLACEHOLDERS. Replacing them is a one-line edit each,
 * and nothing else in the app needs to change.
 *
 * Two names, and no more. They differ in kind, which is what keeps them
 * straight in a person's head:
 *
 *   `name`       the product. A thing. It labels the rail, the login card, the
 *                browser tab and the social card. It appears rarely.
 *   `assistant`  the voice. It speaks in the first person ("I noticed…"), it
 *                is the room in the rail where Talk and Diary live, and it is
 *                what the chat page is titled.
 *
 * Auto-Mom is NOT a third name. It is a component, and it must never appear on
 * screen. Everything it produces speaks as `assistant`.
 */

/** Words. Theme-independent, environment-independent, the whole vocabulary. */
export const BRAND = {
  /**
   * The product / company name. PLACEHOLDER.
   *
   * Keep it four to eight characters. The desktop rail and the mobile top bar
   * are the two places a long name wraps, so render the longest name you are
   * seriously considering in both before committing to it.
   */
  name: 'Kite',

  /**
   * One line under the name, on the login card and the rail header. PLACEHOLDER.
   * Under about 45 characters. It is a promise, not a mission statement.
   */
  tagline: 'your life, one clear move at a time',

  /**
   * The assistant's name — the voice in the copy. PLACEHOLDER.
   *
   * Short and pronounceable: it appears mid-sentence many times a day
   * ("{assistant} noticed…"), and it has to fit the rail as a room name.
   */
  assistant: 'Ori',

  /**
   * What the assistant does, shown under its name in the rail room.
   */
  assistantSub: 'talk it through · diary',
} as const

/**
 * The artwork. PLACEHOLDER files — replace the images at these paths and
 * nothing in the code changes.
 *
 * What to supply:
 *   mark       one SVG on a 512×512 box, artwork inset to ~448 so it survives a
 *              circular mask. The rail draws it at 32 CSS px, so: one shape, no
 *              text inside the mark, no hairline strokes.
 *   lockup     mark + wordmark on a 4:1 box (160×40 works). Drawn ~120px wide.
 *   wordmark   the name alone, for places that already show the mark.
 *   socialCard 1200×630. The current asset is 1600×480, which most platforms
 *              crop badly — re-export it at 1200×630 when the real art lands.
 *
 * Raster exports the shell also wants, from the same mark: 512, 192 and 180
 * (PWA + Apple touch icon) and 32 (favicon).
 */
const LIGHT = '/branding/light'
const DARK = '/branding/dark'

export const BRAND_ASSETS = {
  light: {
    mark: `${LIGHT}/hortonio-icon.svg`,
    markMono: `${LIGHT}/hortonio-icon-mono.svg`,
    lockup: `${LIGHT}/hortonio-horizontal.svg`,
    wordmark: `${LIGHT}/hortonio-wordmark.svg`,
    socialCard: `${LIGHT}/social-card-1600x480.png`,
  },
  dark: {
    mark: `${DARK}/hortonio-icon-dark-transparent.svg`,
    markMono: `${DARK}/hortonio-icon-dark.svg`,
    lockup: `${DARK}/hortonio-horizontal-dark-transparent.svg`,
    wordmark: `${DARK}/hortonio-wordmark-dark-transparent.svg`,
    socialCard: `${DARK}/social-card-dark.png`,
  },
} as const

/**
 * Possessive form of a name, for copy like "{assistant}'s plan".
 * A name already ending in s takes the bare apostrophe.
 */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}’` : `${name}’s`
}

/** "Ori’s" — precomputed because copy reaches for it constantly. */
export const ASSISTANT_POSSESSIVE = possessive(BRAND.assistant)
